import { useLayoutEffect } from 'react';

// Observe newly rendered content too, including pagination and fund detail routes.
export function usePageReveal(pathname, loading) {
  useLayoutEffect(() => {
    const preference = matchMedia('(prefers-reduced-motion: reduce)');
    if (preference.matches || !('IntersectionObserver' in window)) return;
    const root = document.getElementById('root');
    const selector = [
      '.bank-section', '.period-bar', '.list-heading', '.list-columns', '.fund-row', '.load-more',
      '.all-funds-cta', '.back-link', '.page-intro',
      '.detail-heading', '.price-card', '.detail-keymetrics', '.chart-readout', '.big-chart',
      '.chart-dates', '.chart-caption', '.source-caption', '.data-notice',
      '.disclosure>summary', '.disclosure-content>p', '.disclosure-content>.tag-row',
      '.disclosure-content>.source-link', '.holding', '.facts-list>div', '.annual-row',
      '.track-record h3', '.growth-stat>*', '.drawdown>*', '.document-list>a',
      '.section-note', '.empty-state>*', '.site-footer>div>.brand', '.site-footer>div>p',
      '.footer-info>*', '.footer-wordmark'
    ].join(',');
    const tracked = new Set();
    const reveal = element => {
      element.dataset.reveal = 'visible';
    };
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting || entry.target.contains(document.activeElement)) reveal(entry.target);
      });
    }, { threshold: 0, rootMargin: '40px 0px 40px 0px' });
    // A wider exit boundary prevents the translated entrance from retriggering itself.
    const exitObserver = new IntersectionObserver(entries => {
      entries.forEach(entry=>{
        if(!entry.isIntersecting&&!entry.target.contains(document.activeElement))entry.target.dataset.reveal='pending';
      });
    },{threshold:0,rootMargin:'80px 0px 80px 0px'});
    const register = element => {
      if (tracked.has(element)) return;
      tracked.add(element);
      const siblings = Array.from(element.parentElement.children).filter(child => child.matches(selector));
      element.style.setProperty('--reveal-delay', `${Math.min(siblings.indexOf(element), 4) * 65}ms`);
      element.dataset.reveal = preference.matches?'visible':'pending';
      if(!preference.matches){observer.observe(element);exitObserver.observe(element);}
    };
    const scan = scope => {
      if (scope.matches?.(selector)) register(scope);
      scope.querySelectorAll(selector).forEach(register);
    };
    const onFocus = event => {
      let element = event.target.closest('[data-reveal="pending"]');
      while (element) {
        element.style.setProperty('--reveal-delay', '0ms');
        reveal(element);
        element = element.parentElement?.closest('[data-reveal="pending"]');
      }
    };
    const disable = () => {
      if(preference.matches){observer.disconnect();exitObserver.disconnect();tracked.forEach(reveal);}
      else tracked.forEach(element=>{observer.observe(element);exitObserver.observe(element);});
    };
    // Scan only added subtrees: moving over a chart must not rescan the whole page.
    const mutations = new MutationObserver(records => records.forEach(record => {
      record.addedNodes.forEach(node => { if (node.nodeType === 1) scan(node); });
    }));
    scan(root);
    mutations.observe(root, { childList: true, subtree: true });
    root.addEventListener('focusin', onFocus);
    preference.addEventListener('change', disable);
    return () => {
      observer.disconnect();
      exitObserver.disconnect();
      mutations.disconnect();
      root.removeEventListener('focusin', onFocus);
      preference.removeEventListener('change', disable);
      tracked.forEach(element => {
        delete element.dataset.reveal;
        element.style.removeProperty('--reveal-delay');
      });
    };
  }, [pathname, loading]);
}
